import type { Dayjs } from "dayjs";
import axiosInstance from "../../Utils/axiosInstance";
import {
  JobKey,
  JobPatch,
  JobsState,
  PollResult,
  RunParams,
} from "./Initialmarginsummary.types";
import type { MarginBy } from "./initialMargin.types";

/* ---------- constants ---------- */

// Payload formats (same as the Initial Margin page)
export const BUSINESS_DATE_FORMAT = "YYYYMMDD";
export const TILL_DATE_FORMAT = "YYYY-MM-DD";

// Polling of each job (/paper-app/margin/jobs-org/<job_id>/)
export const IM_POLL_INTERVAL_MS = 3000;
// backend allows the calculator 30 min (IRM2_TIMEOUT) plus 2 min task overhead
export const IM_POLL_TIMEOUT_MS = 32 * 60 * 1000;
export const IM_JOB_DONE_STATUSES = ["done", "completed", "complete", "success", "succeeded", "finished"];
export const IM_JOB_FAILED_STATUSES = ["failed", "failure", "error"];

// Columns come from job.buffered_margins
export const IM_RESULT_HEADERS: Record<string, string> = {
  portfolio: "Portfolio Name",
  strategy: "Strategy",
  im1: "IRM1",
  im2: "IRM2",
  irm_sum: "Total IRM",
  buffer_factor: "IRM Factor",
  buffered_im: "IRM+Factor",
};

// numeric columns that are not money (no red/green colouring, no 2-decimal format)
export const NON_MONEY_KEYS = ["buffer_factor"];

export const JOB_SECTIONS: { key: JobKey; title: string }[] = [
  { key: "organization", title: "Organization Books" },
  { key: "counterparty", title: "Counterparty Books" },
];

export const EMPTY_JOBS: JobsState = { organization: null, counterparty: null };

export const STATUS_CHIP: Record<string, { label: string; color: any }> = {
  queued: { label: "Queued", color: "default" },
  running: { label: "Running", color: "info" },
  done: { label: "Done", color: "success" },
  failed: { label: "Failed", color: "error" },
  skipped: { label: "No positions", color: "default" },
};

/* ---------- formatting helpers ---------- */

export const formatMoney = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const moneyColor = (value: number) => (value < 0 ? "#dc2626" : "#16a34a");

/* ---------- API ---------- */

// Both dates are optional; the backend uses its own defaults when they are left out
export const buildRunParams = (
  businessDate: Dayjs | null,
  tillDate: Dayjs | null,
  includeTillDate: boolean,
  marginBy: MarginBy
): RunParams => {
  const params: RunParams = {
    include_till_date: includeTillDate,
    margin_by: marginBy,
  };
  if (businessDate && businessDate.isValid()) {
    params.business_date = businessDate.format(BUSINESS_DATE_FORMAT);
  }
  if (tillDate && tillDate.isValid()) {
    params.till_date = tillDate.format(TILL_DATE_FORMAT);
  }
  return params;
};

// rerun=true repeats the last run's dates as a JSON body with "rerun": true,
// forcing a fresh calculation instead of a reused result
export const startRunAll = async (
  params: RunParams,
  rerun = false
): Promise<any> => {
  if (rerun) {
    const response = await axiosInstance.post("/paper-app/run-im-all/", {
      ...params,
      rerun: true,
    });
    return response.data;
  }

  const formData = new FormData();
  if (params.business_date) formData.append("business_date", params.business_date);
  if (params.till_date) formData.append("till_date", params.till_date);
  formData.append("include_till_date", String(params.include_till_date));
  formData.append("margin_by", params.margin_by);

  const response = await axiosInstance.post("/paper-app/run-im-all/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

// Maps the run-im-all response to the initial state of both job sections
export const buildStartedJobs = (data: any): JobsState => {
  const started: JobsState = { organization: null, counterparty: null };
  JOB_SECTIONS.forEach(({ key }) => {
    const job = data?.[key];
    const jobId = job?.job_id ?? null;
    started[key] = {
      jobId,
      status: jobId ? String(job?.status || "queued").toLowerCase() : "skipped",
      positions: typeof job?.positions === "number" ? job.positions : null,
      margins: [],
      total: null,
      warnings: [],
      error: "",
      reused: (job?.reused ?? data?.reused) === true,
    };
  });
  return started;
};

// Polls one job until it is done or failed; never throws, errors go to onUpdate
export const pollJob = async (
  jobId: string,
  onUpdate: (patch: JobPatch) => void,
  isMounted: () => boolean
): Promise<PollResult> => {
  const startedAt = Date.now();
  let lastStatus = "";
  try {
    while (true) {
      await new Promise((resolve) => setTimeout(resolve, IM_POLL_INTERVAL_MS));
      if (!isMounted()) return "aborted";

      const { data: job } = await axiosInstance.get(
        `/paper-app/margin/jobs-org/${jobId}/`
      );
      if (!isMounted()) return "aborted";

      const jobStatus = String(job?.status ?? "").trim().toLowerCase();
      lastStatus = jobStatus;

      if (IM_JOB_DONE_STATUSES.includes(jobStatus)) {
        onUpdate({
          status: "done",
          // buffered result first; falls back to the old shape
          margins: Array.isArray(job?.buffered_margins)
            ? job.buffered_margins
            : Array.isArray(job?.margins)
              ? job.margins
              : [],
          total:
            typeof job?.buffered_total_margin === "number"
              ? job.buffered_total_margin
              : typeof job?.total_margin_usd === "number"
                ? job.total_margin_usd
                : null,
          warnings: Array.isArray(job?.warnings)
            ? job.warnings.map((w: any) => String(w))
            : [],
        });
        return "done";
      }
      if (IM_JOB_FAILED_STATUSES.includes(jobStatus)) {
        onUpdate({
          status: "failed",
          error: job?.error || "IM calculation failed",
        });
        return "failed";
      }

      onUpdate({ status: jobStatus || "queued" });

      if (Date.now() - startedAt > IM_POLL_TIMEOUT_MS) {
        onUpdate({
          status: "failed",
          error:
            lastStatus === "queued"
              ? "Never started (job still queued). The margin worker may not be running."
              : `Timed out (last status: ${lastStatus || "unknown"}).`,
        });
        return "failed";
      }
    }
  } catch (error: any) {
    if (!isMounted()) return "aborted";
    onUpdate({
      status: "failed",
      error:
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch job status",
    });
    return "failed";
  }
};

export const getRunErrorMessage = (error: any): string =>
  error.response?.data?.detail ||
  error.response?.data?.message ||
  error.response?.data?.error ||
  error.message ||
  "Failed to start IM calculation";